import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { adminSession } from '@/lib/api';
import { api } from '../../../convex/functions/_generated/api';
import type { Id } from '../../../convex/functions/_generated/dataModel';
import { slugify } from '../../../convex/functions/lib/blog/markdown';
import { BLOG_LIMITS, blogError, formatDate, formatDateTime, type PostStatus } from '@/lib/blog';
import {
  Counter,
  ErrorBanner,
  FieldError,
  FieldLabel,
  Segmented,
  StatusBadge,
  dangerButton,
  disabledStyle,
  inputStyle,
  primaryButton,
  secondaryButton,
} from '@/components/BlogUi';

type Tab = 'posts' | 'pillars';
type Filter = 'all' | PostStatus;

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

export function BlogPage() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'pillars' ? 'pillars' : 'posts';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <h2 className="serif" style={{ fontSize: 28, fontWeight: 500, fontStyle: 'italic', margin: 0 }}>
          Blog
        </h2>
        <Link to="/dashboard/blog/new" style={{ ...primaryButton, textDecoration: 'none' }}>
          New post
        </Link>
      </div>

      <div style={{ marginBottom: 18 }}>
        <Segmented<Tab>
          label="Blog sections"
          options={[
            { value: 'posts', label: 'Posts' },
            { value: 'pillars', label: 'Pillars' },
          ]}
          value={tab}
          onChange={(t) => setParams(t === 'posts' ? {} : { tab: t }, { replace: true })}
        />
      </div>

      {tab === 'posts' ? <PostsTab /> : <PillarsTab />}
    </div>
  );
}

// ── Posts ─────────────────────────────────────────────────────────────

function PostsTab() {
  const token = adminSession.getToken() ?? '';
  const [filter, setFilter] = useState<Filter>('all');
  const posts = useQuery(
    api.admin.blog.listPosts,
    token ? { token, ...(filter === 'all' ? {} : { status: filter }) } : 'skip',
  );

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <Segmented<Filter> label="Filter by status" options={FILTERS} value={filter} onChange={setFilter} />
      </div>

      {posts === undefined ? (
        <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
      ) : posts.length === 0 ? (
        <div style={emptyBox}>
          {filter === 'all' ? 'No posts yet.' : `No ${FILTERS.find((f) => f.value === filter)?.label.toLowerCase()}.`}
        </div>
      ) : (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={th}>Title</th>
                <th style={th}>Pillar</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p._id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={td}>
                    {/* The width cap lives on the blocks, not the cell: table cells ignore max-width. */}
                    <Link
                      to={`/dashboard/blog/${p._id}`}
                      style={{ color: 'var(--ink)', fontWeight: 600, display: 'block', maxWidth: 'min(420px, 60vw)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {p.title}
                    </Link>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', maxWidth: 'min(420px, 60vw)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      /blog/{p.slug}
                    </div>
                  </td>
                  <td style={{ ...td, color: 'var(--ink-2)' }}>{p.pillar?.name ?? '—'}</td>
                  <td style={td}>
                    <StatusBadge status={p.status} />
                  </td>
                  <td style={{ ...td, textAlign: 'right', color: 'var(--ink-3)', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {postDate(p)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function postDate(p: { status: PostStatus; publishAt: number | null; publishedAt: number | null; updatedAt: number }): string {
  if (p.status === 'scheduled' && p.publishAt) return formatDateTime(p.publishAt);
  if (p.status === 'published' && p.publishedAt) return formatDate(p.publishedAt);
  return `Edited ${formatDate(p.updatedAt)}`;
}

// ── Pillars ───────────────────────────────────────────────────────────

type Pillar = {
  _id: Id<'blogPillars'>;
  slug: string;
  name: string;
  description: string;
  seoTitle: string | null;
  seoDescription: string | null;
  order: number;
  postCount: number;
};

function PillarsTab() {
  const token = adminSession.getToken() ?? '';
  const pillars = useQuery(api.admin.blog.listPillars, token ? { token } : 'skip');
  const reorder = useMutation(api.admin.blog.reorderPillars);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Id<'blogPillars'> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const sorted = pillars ? [...pillars].sort((a, b) => a.order - b.order) : undefined;

  const move = async (index: number, delta: number) => {
    if (!sorted) return;
    const next = [...sorted];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    setErr(null);
    try {
      await reorder({ token, pillarIds: next.map((p) => p._id) });
    } catch (e) {
      setErr(blogError(e, 'Blog pillars').message);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 820 }}>
      {err && <ErrorBanner>{err}</ErrorBanner>}

      {creating ? (
        <PillarForm token={token} onDone={() => setCreating(false)} />
      ) : (
        <div>
          <button type="button" style={secondaryButton} onClick={() => setCreating(true)}>
            New pillar
          </button>
        </div>
      )}

      {sorted === undefined ? (
        <div style={{ padding: 20, color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
      ) : sorted.length === 0 ? (
        !creating && <div style={emptyBox}>No pillars yet. Every post needs one.</div>
      ) : (
        sorted.map((p, i) =>
          editing === p._id ? (
            <PillarForm key={p._id} token={token} pillar={p} onDone={() => setEditing(null)} />
          ) : (
            <PillarRow
              key={p._id}
              token={token}
              pillar={p}
              canUp={i > 0}
              canDown={i < sorted.length - 1}
              onUp={() => move(i, -1)}
              onDown={() => move(i, 1)}
              onEdit={() => setEditing(p._id)}
            />
          ),
        )
      )}
    </div>
  );
}

function PillarRow({
  token,
  pillar,
  canUp,
  canDown,
  onUp,
  onDown,
  onEdit,
}: {
  token: string;
  pillar: Pillar;
  canUp: boolean;
  canDown: boolean;
  onUp: () => void;
  onDown: () => void;
  onEdit: () => void;
}) {
  const remove = useMutation(api.admin.blog.deletePillar);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const doDelete = async () => {
    setBusy(true);
    setErr(null);
    try {
      await remove({ token, pillarId: pillar._id });
    } catch (e) {
      const be = blogError(e, 'Blog pillars');
      setErr(
        be.code === 'PILLAR_IN_USE'
          ? `${pillar.name} still has ${pillar.postCount === 1 ? '1 post' : `${pillar.postCount} posts`}. Move ${pillar.postCount === 1 ? 'it' : 'them'} to another pillar first.`
          : be.message,
      );
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <button type="button" aria-label={`Move ${pillar.name} up`} disabled={!canUp} onClick={onUp} style={{ ...arrowButton, ...disabledStyle(!canUp) }}>
            ▲
          </button>
          <button type="button" aria-label={`Move ${pillar.name} down`} disabled={!canDown} onClick={onDown} style={{ ...arrowButton, ...disabledStyle(!canDown) }}>
            ▼
          </button>
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{pillar.name}</div>
          <div className="mono" style={{ fontSize: 11, color: 'var(--ink-4)', overflowWrap: 'anywhere' }}>
            /blog/topics/{pillar.slug} · {pillar.postCount === 1 ? '1 post' : `${pillar.postCount} posts`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end', marginLeft: 'auto' }}>
          {confirming ? (
            <>
              <button type="button" style={secondaryButton} onClick={() => setConfirming(false)} disabled={busy}>
                Cancel
              </button>
              <button type="button" style={{ ...dangerButton, ...disabledStyle(busy) }} onClick={doDelete} disabled={busy}>
                {busy ? 'Deleting…' : `Delete ${pillar.name}`}
              </button>
            </>
          ) : (
            <>
              <button type="button" style={secondaryButton} onClick={onEdit}>
                Edit
              </button>
              <button type="button" style={dangerButton} onClick={() => setConfirming(true)}>
                Delete
              </button>
            </>
          )}
        </div>
      </div>
      {err && (
        <div style={{ marginTop: 10 }}>
          <ErrorBanner>{err}</ErrorBanner>
        </div>
      )}
    </div>
  );
}

function PillarForm({
  token,
  pillar,
  onDone,
}: {
  token: string;
  pillar?: Pillar;
  onDone: () => void;
}) {
  const save = useMutation(api.admin.blog.savePillar);
  const [name, setName] = useState(pillar?.name ?? '');
  const [slug, setSlug] = useState(pillar?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(Boolean(pillar));
  const [description, setDescription] = useState(pillar?.description ?? '');
  const [seoTitle, setSeoTitle] = useState(pillar?.seoTitle ?? '');
  const [seoDescription, setSeoDescription] = useState(pillar?.seoDescription ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ code: string; message: string } | null>(null);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  const submit = async () => {
    if (!name.trim()) {
      setErr({ code: 'INVALID_NAME', message: 'Give the pillar a name.' });
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await save({
        token,
        ...(pillar ? { pillarId: pillar._id } : {}),
        fields: {
          name: name.trim(),
          ...(effectiveSlug ? { slug: effectiveSlug } : {}),
          description: description.trim(),
          seoTitle: seoTitle.trim() || null,
          seoDescription: seoDescription.trim() || null,
        },
      });
      onDone();
    } catch (e) {
      const be = blogError(e, 'Blog pillars');
      setErr({ code: be.code, message: be.message });
    } finally {
      setBusy(false);
    }
  };

  const errFor = (codes: string[]) => (err && codes.includes(err.code) ? err.message : null);
  const general = err && !['INVALID_NAME', 'SLUG_TAKEN', 'INVALID_SLUG', 'INVALID_DESCRIPTION', 'INVALID_SEO_TITLE', 'INVALID_SEO_DESCRIPTION'].includes(err.code);
  const slugChanged = pillar && effectiveSlug !== pillar.slug;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      style={{ background: 'var(--surface)', border: '1px solid var(--line-strong)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div style={{ fontWeight: 600 }}>{pillar ? `Edit ${pillar.name}` : 'New pillar'}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        <div>
          <FieldLabel htmlFor="pillar-name">Name</FieldLabel>
          <input id="pillar-name" value={name} maxLength={BLOG_LIMITS.pillarNameMax} onChange={(e) => setName(e.target.value)} style={inputStyle} autoFocus />
          <FieldError message={errFor(['INVALID_NAME'])} />
        </div>
        <div>
          <FieldLabel htmlFor="pillar-slug">Slug</FieldLabel>
          <input
            id="pillar-slug"
            value={effectiveSlug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value.toLowerCase());
            }}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: 13 }}
          />
          <FieldError message={errFor(['SLUG_TAKEN', 'INVALID_SLUG'])} />
          {slugChanged && !errFor(['SLUG_TAKEN', 'INVALID_SLUG']) && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>/blog/topics/{pillar.slug} will redirect here.</div>
          )}
        </div>
      </div>
      <div>
        <FieldLabel htmlFor="pillar-desc" aside={<Counter n={description.length} max={BLOG_LIMITS.pillarDescriptionMax} />}>
          Description
        </FieldLabel>
        <textarea
          id="pillar-desc"
          value={description}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <FieldError message={errFor(['INVALID_DESCRIPTION'])} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        <div>
          <FieldLabel htmlFor="pillar-seo-title" aside={<Counter n={seoTitle.length} max={BLOG_LIMITS.seoTitleMax} />}>
            SEO title
          </FieldLabel>
          <input id="pillar-seo-title" value={seoTitle} placeholder={name} onChange={(e) => setSeoTitle(e.target.value)} style={inputStyle} />
          <FieldError message={errFor(['INVALID_SEO_TITLE'])} />
        </div>
        <div>
          <FieldLabel htmlFor="pillar-seo-desc" aside={<Counter n={seoDescription.length} max={BLOG_LIMITS.seoDescriptionMax} />}>
            SEO description
          </FieldLabel>
          <input id="pillar-seo-desc" value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} style={inputStyle} />
          <FieldError message={errFor(['INVALID_SEO_DESCRIPTION'])} />
        </div>
      </div>
      {general && <ErrorBanner>{err.message}</ErrorBanner>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" style={secondaryButton} onClick={onDone} disabled={busy}>
          Cancel
        </button>
        <button type="submit" style={{ ...primaryButton, ...disabledStyle(busy) }} disabled={busy}>
          {busy ? 'Saving…' : pillar ? 'Save pillar' : 'Create pillar'}
        </button>
      </div>
    </form>
  );
}

const emptyBox: React.CSSProperties = {
  padding: 24,
  color: 'var(--ink-3)',
  fontSize: 13,
  textAlign: 'center',
  background: 'var(--surface)',
  borderRadius: 12,
  border: '1px solid var(--line)',
};

const th: React.CSSProperties = {
  padding: '10px 14px',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--ink-3)',
  textTransform: 'uppercase',
  letterSpacing: 0.6,
};

const td: React.CSSProperties = { padding: '10px 14px', verticalAlign: 'middle' };

const arrowButton: React.CSSProperties = {
  width: 28,
  height: 22,
  fontSize: 9,
  lineHeight: 1,
  color: 'var(--ink-3)',
  border: '1px solid var(--line)',
  borderRadius: 4,
  background: 'var(--surface-2)',
};
